
import React, { useState } from 'react';
import { Dispute, Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { Gavel, AlertCircle, FileText, CheckCircle, ThumbsUp, ThumbsDown, Lock, Bot, ShieldCheck, TrendingUp, MessageSquare, History, X } from 'lucide-react';

interface DisputeResolutionViewProps {
  language: Language;
  disputes: Dispute[];
}

export const DisputeResolutionView: React.FC<DisputeResolutionViewProps> = ({ language, disputes }) => {
  const t = TRANSLATIONS[language];
  // Use local state to handle updates immediately in the UI for the demo
  const [localDisputes, setLocalDisputes] = useState<Dispute[]>(disputes);
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  
  // Admin Reasoning State
  const [resolvingSide, setResolvingSide] = useState<'initiator' | 'respondent' | null>(null);
  const [reasonInput, setReasonInput] = useState('');

  const handleStartResolution = (side: 'initiator' | 'respondent') => {
      setResolvingSide(side);
  };

  const cancelResolution = () => {
      setResolvingSide(null);
      setReasonInput('');
  };

  const confirmResolution = () => {
      if (!selectedDispute || !resolvingSide || !reasonInput.trim()) return;

      const updatedDispute: Dispute = {
          ...selectedDispute,
          status: resolvingSide === 'initiator' ? 'RESOLVED_REFUND' : 'RESOLVED_RELEASE',
          adminReason: reasonInput,
          resolvedDate: new Date().toISOString().split('T')[0]
      };

      const updatedList = localDisputes.map(d => d.id === selectedDispute.id ? updatedDispute : d);
      
      setLocalDisputes(updatedList);
      setSelectedDispute(updatedDispute);
      setResolvingSide(null);
      setReasonInput('');
      
      alert(`Dispute Resolved. Funds have been distributed.`);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
           <Gavel className="text-rose-500" />
           {t.disputeCenter}
        </h1>
        <p className="text-slate-400 mt-1">{t.disputeCenterSubtitle}</p>
      </div>

      {/* Dispute List (Full Width) */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-700 bg-slate-900/50">
              <h3 className="font-bold text-white">{t.activeDisputes}</h3>
          </div>
          <div className="p-2 space-y-2">
              {localDisputes.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No active disputes</div>
              ) : (
                localDisputes.map(dispute => (
                  <div 
                    key={dispute.id}
                    onClick={() => { setSelectedDispute(dispute); setResolvingSide(null); setReasonInput(''); }}
                    className="p-4 rounded-lg cursor-pointer bg-slate-700/30 border border-slate-700 hover:bg-slate-700 hover:border-indigo-500 transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
                  >
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                            <span className="text-xs font-mono text-slate-500">#{dispute.id.slice(-4)}</span>
                            <span className={`text-xs px-2 py-0.5 rounded uppercase font-bold ${
                                dispute.status === 'OPEN' ? 'bg-rose-500/20 text-rose-400' : 'bg-emerald-500/20 text-emerald-400'
                            }`}>
                                {dispute.status.replace('RESOLVED_', '')}
                            </span>
                        </div>
                        <h4 className="text-white font-bold text-sm mb-1">{dispute.reason}</h4>
                        <div className="text-xs text-slate-400 mt-1 flex gap-2">
                             <span>{dispute.dateCreated}</span>
                             <span>•</span>
                             <span>{dispute.initiatorName} vs {dispute.respondentName}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                          <div className="text-right">
                              <span className="text-xs text-slate-500 block">{t.amountLocked}</span>
                              <span className="text-white font-bold font-mono">${dispute.amountLocked}</span>
                          </div>
                          <button className="bg-slate-600 hover:bg-indigo-600 text-white p-2 rounded-lg transition-colors">
                              <FileText size={16} />
                          </button>
                      </div>
                  </div>
              )))}
          </div>
      </div>

      {/* Dispute Detail Modal */}
      {selectedDispute && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-4xl shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                  {/* Modal Header */}
                  <div className="flex justify-between items-center p-6 border-b border-slate-700 bg-slate-900/50 rounded-t-xl">
                      <div>
                          <h2 className="text-xl font-bold text-white flex items-center gap-2">
                              {selectedDispute.reason}
                              <span className="text-xs font-normal text-slate-400 font-mono">#{selectedDispute.id}</span>
                          </h2>
                          <div className="flex items-center gap-2 text-sm text-slate-400 mt-1">
                              <span className="font-bold text-white">{selectedDispute.initiatorName}</span>
                              <span className="text-slate-600 text-xs uppercase px-1">Disputing</span>
                              <span className="font-bold text-white">{selectedDispute.respondentName}</span>
                          </div>
                      </div>
                      <button onClick={() => setSelectedDispute(null)} className="text-slate-400 hover:text-white bg-slate-800 p-2 rounded-lg transition-colors border border-slate-700">
                          <X size={20} />
                      </button>
                  </div>

                  {/* Scrollable Modal Content */}
                  <div className="p-6 overflow-y-auto">
                      <div className="space-y-6">
                          {/* Locked Amount Badge */}
                          <div className="flex items-center gap-2 bg-slate-900 px-4 py-3 rounded-lg border border-slate-700 w-fit">
                              <Lock size={16} className="text-amber-500" />
                              <span className="text-sm font-bold text-white">${selectedDispute.amountLocked} {t.amountLocked}</span>
                          </div>

                          {/* AI Analysis Card */}
                          {selectedDispute.aiAnalysis && (
                              <div className="bg-gradient-to-r from-indigo-900/40 to-slate-900/40 border border-indigo-500/30 rounded-xl p-5 relative overflow-hidden group">
                                  <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-bl-full -mr-8 -mt-8 pointer-events-none"></div>
                                  
                                  <div className="flex items-center gap-2 mb-4">
                                      <div className="p-1.5 bg-indigo-500 rounded-lg shadow-lg shadow-indigo-500/20">
                                          <Bot size={18} className="text-white" />
                                      </div>
                                      <h3 className="text-lg font-bold text-white">{t.aiJudge}</h3>
                                      <span className="text-xs text-indigo-300 border border-indigo-500/30 px-2 py-0.5 rounded-full bg-indigo-500/10">PRELIMINARY</span>
                                  </div>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                                      <div>
                                          <p className="text-xs text-slate-400 uppercase font-bold mb-1">{t.aiRecommendation}</p>
                                          <div className={`flex items-center gap-2 text-lg font-bold ${
                                              selectedDispute.aiAnalysis.recommendation === 'REFUND' 
                                              ? 'text-rose-400' 
                                              : selectedDispute.aiAnalysis.recommendation === 'RELEASE' 
                                                  ? 'text-emerald-400' 
                                                  : 'text-amber-400'
                                          }`}>
                                              {selectedDispute.aiAnalysis.recommendation === 'REFUND' ? (
                                                  <TrendingUp className="rotate-180" size={20} />
                                              ) : (
                                                  <ShieldCheck size={20} />
                                              )}
                                              {selectedDispute.aiAnalysis.recommendation === 'REFUND' ? 'Refund Project' : 'Release to KOL'}
                                          </div>
                                      </div>
                                      <div>
                                          <p className="text-xs text-slate-400 uppercase font-bold mb-1">{t.aiConfidence}</p>
                                          <div className="flex items-center gap-3">
                                              <div className="flex-1 h-3 bg-slate-700 rounded-full overflow-hidden">
                                                  <div 
                                                    className={`h-full rounded-full ${
                                                        selectedDispute.aiAnalysis.confidenceScore > 80 ? 'bg-emerald-500' : 'bg-amber-500'
                                                    }`} 
                                                    style={{ width: `${selectedDispute.aiAnalysis.confidenceScore}%` }}
                                                  ></div>
                                              </div>
                                              <span className="text-sm font-bold text-white">{selectedDispute.aiAnalysis.confidenceScore}%</span>
                                          </div>
                                      </div>
                                  </div>
                                  
                                  <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700/50">
                                      <p className="text-xs text-slate-500 uppercase font-bold mb-1">{t.aiReasoning}</p>
                                      <p className="text-sm text-slate-300 leading-relaxed italic">
                                          "{selectedDispute.aiAnalysis.reasoning}"
                                      </p>
                                  </div>
                              </div>
                          )}

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Chat Transcript Panel */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                    <History size={16} className="text-indigo-400" />
                                    {t.chatHistory}
                                </h3>
                                {selectedDispute.chatTranscript && (
                                    <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 max-h-60 overflow-y-auto space-y-3">
                                        {selectedDispute.chatTranscript.map(msg => (
                                            <div key={msg.id} className={`flex ${msg.senderId === 'proj' ? 'justify-start' : 'justify-end'}`}>
                                                <div className={`max-w-[90%] p-3 rounded-lg text-xs ${
                                                    msg.senderId === 'proj' 
                                                    ? 'bg-slate-800 text-slate-200 rounded-tl-none border border-slate-700' 
                                                    : 'bg-indigo-900/30 text-indigo-100 rounded-tr-none border border-indigo-500/30'
                                                }`}>
                                                    <p className="font-bold mb-1 opacity-70">{msg.senderId === 'proj' ? selectedDispute.initiatorName : selectedDispute.respondentName}</p>
                                                    <p>{msg.text}</p>
                                                    <p className="text-[10px] opacity-50 mt-1 text-right">{msg.timestamp}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Evidence Files */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                    <FileText size={16} className="text-indigo-400" />
                                    {t.evidence}
                                </h3>
                                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                                     <div className="flex gap-2 flex-wrap">
                                        {selectedDispute.evidence.map((ev, i) => (
                                            <div key={i} className="bg-slate-700 px-3 py-2 rounded-lg text-xs text-slate-200 flex items-center gap-2 cursor-pointer hover:bg-slate-600 transition-colors w-full">
                                                <FileText size={14} className="text-slate-400" />
                                                {ev}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                          </div>

                          {/* Admin Decision Area */}
                          <div className="pt-6 border-t border-slate-700">
                              <h3 className="text-sm font-bold text-white mb-4 text-center uppercase tracking-widest text-slate-500">
                                  {t.adminDecision}
                              </h3>
                              
                              {selectedDispute.status === 'OPEN' ? (
                                  !resolvingSide ? (
                                      <div className="flex flex-col sm:flex-row gap-4">
                                          <button 
                                            onClick={() => handleStartResolution('initiator')}
                                            className="flex-1 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-300 py-4 rounded-xl flex flex-col items-center gap-2 transition-all hover:scale-[1.02]"
                                          >
                                              <ThumbsUp className="rotate-180" size={24} />
                                              <span className="font-bold">{t.finalizeRefund}</span>
                                              <span className="text-xs opacity-70">Winner: {selectedDispute.initiatorName}</span>
                                          </button>
                                          
                                          <button 
                                            onClick={() => handleStartResolution('respondent')}
                                            className="flex-1 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 py-4 rounded-xl flex flex-col items-center gap-2 transition-all hover:scale-[1.02]"
                                          >
                                              <CheckCircle size={24} />
                                              <span className="font-bold">{t.finalizeRelease}</span>
                                              <span className="text-xs opacity-70">Winner: {selectedDispute.respondentName}</span>
                                          </button>
                                      </div>
                                  ) : (
                                      <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-6 animate-in fade-in slide-in-from-bottom-2">
                                          <div className="flex justify-between items-center mb-4">
                                              <h4 className="font-bold text-white flex items-center gap-2">
                                                  {resolvingSide === 'initiator' 
                                                    ? <span className="text-rose-400">Ruling: Refund Project</span>
                                                    : <span className="text-emerald-400">Ruling: Release to KOL</span>
                                                  }
                                              </h4>
                                              <button onClick={cancelResolution} className="text-slate-400 hover:text-white">
                                                  <X size={20} />
                                              </button>
                                          </div>
                                          
                                          <label className="block text-sm font-medium text-slate-300 mb-2">
                                              {t.adminReasonLabel} <span className="text-rose-400">*</span>
                                          </label>
                                          <textarea 
                                              value={reasonInput}
                                              onChange={(e) => setReasonInput(e.target.value)}
                                              placeholder={t.enterReasonPlaceholder}
                                              rows={4}
                                              className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white focus:outline-none focus:border-indigo-500 mb-4"
                                          />
                                          
                                          <div className="flex justify-end gap-3">
                                              <button 
                                                  onClick={cancelResolution}
                                                  className="px-4 py-2 text-slate-400 hover:text-white"
                                              >
                                                  {t.cancel}
                                              </button>
                                              <button 
                                                  onClick={confirmResolution}
                                                  disabled={!reasonInput.trim()}
                                                  className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold transition-colors"
                                              >
                                                  {t.confirmVerdict}
                                              </button>
                                          </div>
                                      </div>
                                  )
                              ) : (
                                  // Resolved View
                                  <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-6">
                                      <div className="flex items-center gap-3 mb-4">
                                          <div className={`p-2 rounded-full ${selectedDispute.status === 'RESOLVED_REFUND' ? 'bg-rose-500/20 text-rose-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                                              {selectedDispute.status === 'RESOLVED_REFUND' ? <ThumbsUp className="rotate-180" size={24} /> : <CheckCircle size={24} />}
                                          </div>
                                          <div>
                                              <h4 className="font-bold text-white text-lg">
                                                  {selectedDispute.status === 'RESOLVED_REFUND' ? 'Resolved: Refunded to Project' : 'Resolved: Released to KOL'}
                                              </h4>
                                              <p className="text-xs text-slate-400">{t.resolvedOn} {selectedDispute.resolvedDate}</p>
                                          </div>
                                      </div>
                                      
                                      <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                                          <p className="text-xs text-slate-500 uppercase font-bold mb-2">{t.verdictReason}</p>
                                          <p className="text-slate-300 italic">"{selectedDispute.adminReason}"</p>
                                      </div>
                                  </div>
                              )}
                          </div>
                      </div>
                  </div>
            </div>
        </div>
      )}
    </div>
  );
};
